import {
  getDefaultPttBrowserCode,
  normalizePttCode,
} from "@bezoned/shared/features/push-to-talk/push-to-talk.utils.js";
import { create } from "zustand";
import type { PersistStorage, StorageValue } from "zustand/middleware";
import { persist } from "zustand/middleware";

const DEFAULT_PTT_BROWSER_CODE = getDefaultPttBrowserCode();
const PUSH_TO_TALK_STORAGE_KEY = "bezoned-push-to-talk";

interface PushToTalkStore {
  code: string;
  setCode: (code: string) => void;
  resetToDefault: () => void;
}

type PushToTalkPersistedState = Pick<PushToTalkStore, "code">;

const createStorageValue = (
  code: string,
  version?: number
): StorageValue<PushToTalkPersistedState> => ({
  state: { code },
  ...(version === undefined ? {} : { version }),
});

const createPushToTalkStorage =
  (): PersistStorage<PushToTalkPersistedState> => ({
    getItem: (name) => {
      if (typeof window === "undefined") {
        return null;
      }

      const rawValue = window.localStorage.getItem(name);
      if (!rawValue) {
        return null;
      }

      try {
        const parsedValue = JSON.parse(rawValue) as Partial<
          StorageValue<PushToTalkPersistedState>
        > | null;
        const storedCode =
          typeof parsedValue?.state?.code === "string"
            ? parsedValue.state.code
            : null;

        if (storedCode) {
          const normalizedCode = normalizePttCode(storedCode);
          return createStorageValue(normalizedCode, parsedValue?.version);
        }

        return createStorageValue(
          DEFAULT_PTT_BROWSER_CODE,
          parsedValue?.version
        );
      } catch {
        return createStorageValue(DEFAULT_PTT_BROWSER_CODE);
      }
    },
    removeItem: (name) => {
      if (typeof window === "undefined") {
        return;
      }
      window.localStorage.removeItem(name);
    },
    setItem: (name, value) => {
      if (typeof window === "undefined") {
        return;
      }
      window.localStorage.setItem(name, JSON.stringify(value));
    },
  });

export const usePushToTalkStore = create<PushToTalkStore>()(
  persist<PushToTalkStore, [], [], PushToTalkPersistedState>(
    (set) => ({
      code: DEFAULT_PTT_BROWSER_CODE,

      resetToDefault: () => {
        set({ code: DEFAULT_PTT_BROWSER_CODE });
      },

      setCode: (code: string) => {
        const normalizedCode = normalizePttCode(code);
        set({ code: normalizedCode });
      },
    }),
    {
      name: PUSH_TO_TALK_STORAGE_KEY,
      partialize: (state) => ({
        code: state.code,
      }),
      storage: createPushToTalkStorage(),
    }
  )
);
